// src/pages/GuidelineViewPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';

// Import the display components
import AccordionDisplay from '../components/guidelines/AccordionDisplay';
import TimelineDisplay from '../components/guidelines/TimelineDisplay';
import ContactInfoDisplay from '../components/guidelines/ContactInfoDisplay';

interface ContentSection {
  id: string;
  title: string;
  content: string;
}

interface TimelineEvent {
  id: string;
  title: string;
  date: string;
  color: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  icon: string;
}

interface ContactInfo {
  id: string;
  type: string;
  value: string;
  link?: string;
}

interface GuidelineVersion {
  title: string;
  subtitle?: string;
  contentSections: ContentSection[];
  timeline: TimelineEvent[];
  contactInfo: ContactInfo[];
  createdAt?: Date;
  createdBy?: string;
}

interface Strand {
  _id: string;
  name: string;
  slug: string;
  status?: string;
  endDate?: string;
}

interface Guideline {
  _id?: string;
  strandId: string | Strand;
  currentVersion: number;
  versions: GuidelineVersion[];
  isPublished: boolean;
  publishedVersion?: number;
  publishedAt?: Date;
  applicationLink?: string;
  deadline?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

const strandRoutes: { [key: string]: string } = {
  'Better Live': '/recommend',
  'Navigator': '/navigator',
  'Ban Bam': '/banbam',
};

const getRemainingDays = (endDate: string) => {
  const now = new Date();
  const deadline = new Date(endDate);
  deadline.setHours(18, 0, 0, 0);
  const diffTime = deadline.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const GuidelineViewPage: React.FC = () => {
  const { strandSlug } = useParams<{ strandSlug: string }>();
  const [searchParams] = useSearchParams();
  const requestedVersion = searchParams.get('version');

  const [guideline, setGuideline] = useState<Guideline | null>(null);
  const [strand, setStrand] = useState<Strand | null>(null);
  const [formsMap, setFormsMap] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchAll = async () => {
      if (!strandSlug) return;

      setLoading(true);
      setError(null);

      try {
        const [guidelineRes, strandsRes, formsRes] = await Promise.all([
          axiosInstance.get(`/api/guidelines/strand/${strandSlug}`),
          axiosInstance.get('/api/strands?published=true'),
          axiosInstance.get('/api/formTemplates'),
        ]);

        const guidelineData: Guideline = guidelineRes.data;
        setGuideline(guidelineData);

        const publishedStrands: Strand[] = strandsRes.data || [];
        const matchedStrand = publishedStrands.find((s) => s.slug === strandSlug) || null;
        setStrand(matchedStrand);

        const formsData = formsRes.data || [];
        const mapping: Record<string, any> = {};
        formsData.forEach((form: any) => {
          if (form?.strand) mapping[form.strand] = form;
        });
        setFormsMap(mapping);
      } catch (err: any) {
        setError(err.response?.data?.message || 'An error occurred while fetching guidelines');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [strandSlug]);

  const handleEdit = () => {
    if (guideline?._id) navigate(`/guidelines/${guideline._id}/edit`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleApply = () => {
    if (!strand) return;

    const form = formsMap[strand._id];
    if (form && form.slug) {
      navigate(`/form/${form.slug}`);
      return;
    }

    const route = strandRoutes[strand.name] || '/recommend';
    navigate(route);
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Box my={4}>
          <Typography variant="h4" color="error" gutterBottom>
            {error}
          </Typography>
          <Button variant="contained" color="primary" onClick={handleBack}>
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  if (!guideline || !guideline.versions || guideline.versions.length === 0) {
    return (
      <Container>
        <Box my={4}>
          <Typography variant="h4" gutterBottom>
            No guidelines available
          </Typography>
          <Button variant="contained" color="primary" onClick={handleBack}>
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  let currentVersion: GuidelineVersion | undefined;
  if (requestedVersion) {
    currentVersion = guideline.versions[0];
  } else if (guideline.isPublished && guideline.publishedVersion) {
    currentVersion = guideline.versions[0];
  } else {
    currentVersion = guideline.versions[0];
  }

  if (!currentVersion) {
    return (
      <Container>
        <Box my={4}>
          <Typography variant="h4" gutterBottom>
            Guidelines version not found
          </Typography>
          <Button variant="contained" color="primary" onClick={handleBack}>
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  const strandName =
    typeof guideline.strandId === 'object' && guideline.strandId && 'name' in guideline.strandId
      ? guideline.strandId.name
      : strand?.name || 'Unknown Strand';

  const hasForm = !!(strand?._id && formsMap[strand._id]);
  const remainingDays = strand?.endDate ? getRemainingDays(strand.endDate) : -1;
  const isOpen = (strand?.status === 'confirmed') && remainingDays > 0;
  const applyDisabled = !strand || !hasForm || !isOpen;

  return (
    <Container maxWidth="md">
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h3">{strandName}</Typography>
          {(user?.role === 'admin' || user?.role === 'gm') && guideline._id && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit Guidelines
            </Button>
          )}
        </Box>

        {strand && hasForm && (
          <Box my={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApply}
              disabled={applyDisabled}
            >
              Apply Now
            </Button>
          </Box>
        )}

        {!hasForm && guideline.applicationLink && (
          <Box my={2}>
            <Button
              variant="contained"
              color="primary"
              component="a"
              href={guideline.applicationLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Apply Now
            </Button>
          </Box>
        )}

        <Typography variant="h4" gutterBottom>
          {currentVersion.title}
        </Typography>

        {currentVersion.subtitle && (
          <Typography variant="h5" gutterBottom>
            {currentVersion.subtitle}
          </Typography>
        )}

        {currentVersion.contentSections && currentVersion.contentSections.length > 0 && (
          <Box my={4}>
            <Typography variant="h5" gutterBottom>
              Information
            </Typography>
            <Divider />
            <AccordionDisplay sections={currentVersion.contentSections} />
          </Box>
        )}

        {currentVersion.timeline && currentVersion.timeline.length > 0 && (
          <Box my={4}>
            <Typography variant="h5" gutterBottom>
              Timeline
            </Typography>
            <Divider />
            <TimelineDisplay events={currentVersion.timeline} />
          </Box>
        )}

        {currentVersion.contactInfo && currentVersion.contactInfo.length > 0 && (
          <Box my={4}>
            <Typography variant="h5" gutterBottom>
              Contact Information
            </Typography>
            <Divider />
            <ContactInfoDisplay contactInfo={currentVersion.contactInfo} />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default GuidelineViewPage;